import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { AppShellUIService } from '@campus/components';
import { AuthService, BreadcrumbLinkInterface, Permissions, PersonInterface, SchoolInterface } from '@campus/dal';
import { CalculatorComponent } from '@campus/geogebra';
import { AppShellService, HeaderViewModel, QuickLinkTypeEnum } from '@campus/shared';
import { DropdownMenuItemInterface } from '@campus/ui';
import { dcrIconService, dcrLogoService } from '@diekeure/dcr-components';
import '@diekeure/dcr-components/dcr-avatar.js';
import '@diekeure/dcr-components/dcr-inline-icon-button.js';
import '@diekeure/dcr-components/dcr-logo.js';
import '@diekeure/dcr-components/dcr-menu-item.js';
import '@diekeure/dcr-components/dcr-menu.js';
import '@diekeure/dcr-components/dcr-sub-menu.js';
import '@diekeure/dcr-components/dcr-top-app-bar.js';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, take, takeUntil } from 'rxjs/operators';
import { logos } from '../../../assets/svgs/logos';
import { AppViewModel } from '../../app.viewmodel';
import { icons } from './icons';

dcrLogoService.registerLogos(logos);
dcrIconService.registerIcons(icons);

@Component({
  standalone: false,
  selector: 'campus-polpo-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);

  public Permissions = { ...Permissions.settings, ...Permissions.feature };
  public QuickLinkTypeEnum = QuickLinkTypeEnum;

  public websiteUrl: string;

  public enableGlobalSearch: boolean;

  public activeHistoryPanel = -1;
  public activeFavoritesPanel = -1;

  public isOnV2SchoolDetailRoute$: Observable<boolean>;
  public schoolForm: FormGroup = new FormGroup({
    currentSchool: new FormControl(null),
  });
  public currentSchool$: Observable<SchoolInterface>;
  public schools$: Observable<SchoolInterface[]>;

  public isOnV1Route$: Observable<boolean>;
  public isOnV2Route$: Observable<boolean>;

  public isOnHomeRoute$: Observable<boolean>;

  public pageTitle$: Observable<string>;
  public breadcrumbs$: Observable<BreadcrumbLinkInterface[]>;
  public currentUser$: Observable<PersonInterface>;
  public avatarMenuItems$: Observable<DropdownMenuItemInterface[]>;

  public theme$: Observable<string>;

  @HostBinding('$.is-desktop.attr')
  @HostListener('$.is-desktop.attr')
  public isDesktop$: Observable<boolean>;
  public isHandset$: Observable<boolean>;

  private router: Router = inject(Router);
  private appViewModel: AppViewModel = inject(AppViewModel);
  private headerViewModel: HeaderViewModel = inject(HeaderViewModel);
  private appShellService: AppShellService = inject(AppShellService);
  private appShellUIService: AppShellUIService = inject(AppShellUIService);
  private auth = inject(AuthService);

  private destroyed$ = new Subject<void>();

  public ngOnInit(): void {
    this.loadFeatureToggles();
    this.setupStreams();
    this.setupForm();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onManageFavoritesClick(): void {
    this.openFavoritesPanel();
    this.closeHistoryPanel();
  }

  public onManageHistoryClick(): void {
    this.openHistoryPanel();
    this.closeFavoritesPanel();
  }

  public onGlobalSearchClick(): void {
    this.isOnV2Route$.pipe(take(1)).subscribe((isOnV2Route) => {
      const url = isOnV2Route ? ['v2', 'search'] : ['search'];
      this.router.navigate(url);
    });
  }

  public toggleMenu() {
    this.appShellUIService.openMenu(true);
  }

  public openFavoritesPanel(): void {
    this.activeFavoritesPanel = 0;
  }

  public closeFavoritesPanel(): void {
    this.activeFavoritesPanel = -1;
  }

  public openHistoryPanel(): void {
    this.activeHistoryPanel = 0;
  }

  public closeHistoryPanel(): void {
    this.activeHistoryPanel = -1;
  }

  public openCalculator() {
    return this.dialog.open(CalculatorComponent, {
      data: {},
      panelClass: 'ui-geogebra--fullscreen',
      id: 'geogebraDialogId',
    });
  }

  private loadFeatureToggles() {
    this.enableGlobalSearch = this.headerViewModel.enableGlobalSearch;
  }

  private setupStreams() {
    this.isOnV1Route$ = this.appShellService.isOnV1Route$;
    this.isOnV2Route$ = this.appShellService.isOnV2Route$;

    this.isOnV2Route$.pipe(takeUntil(this.destroyed$)).subscribe((isOnV2Route) => {
      this.websiteUrl = isOnV2Route ? '/v2/home' : '/home';
    });

    this.schools$ = this.appViewModel.schools$;
    this.currentSchool$ = this.appViewModel.currentSchool$;

    this.isOnV2SchoolDetailRoute$ = this.getIsOnV2SchoolDetailRoute$();

    this.isOnHomeRoute$ = combineLatest([
      this.isOnV2Route$,
      // Add startWith to include initial route
      this.router.events.pipe(
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ),
    ]).pipe(
      map(([isOnV2Route, event]) => {
        const route = event.urlAfterRedirects;
        return isOnV2Route ? this.isHomeRoute(route) : this.isHomeRoute(route);
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.schoolForm
      .get('currentSchool')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((school) => this.onFormValueChanges(school));

    this.pageTitle$ = combineLatest([this.appViewModel.pageHeaderDetails$, this.isOnHomeRoute$]).pipe(
      map(([pageHeaderDetails, isHome]) => (isHome ? '' : pageHeaderDetails.title)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.breadcrumbs$ = this.appViewModel.breadcrumbs$;
    this.currentUser$ = this.appViewModel.currentUser$;
    this.avatarMenuItems$ = this.getAvatarMenuItems$();

    this.isDesktop$ = this.appShellUIService.isDesktop$;
    this.isHandset$ = this.appShellUIService.isHandset$;
  }

  private setupForm() {
    this.currentSchool$.pipe(takeUntil(this.destroyed$)).subscribe((school) => {
      this.schoolForm.patchValue({ currentSchool: school }, { emitEvent: false });
    });
  }

  private getAvatarMenuItems$() {
    return combineLatest([
      this.headerViewModel.profileMenuItems$,
      this.appShellUIService.isHandset$,
      this.auth.getPermissions(),
      this.isOnV2Route$,
    ]).pipe(
      map(([menuItems, isHandset, permissions, isOnV2]) => {
        if (!isHandset || permissions.includes(Permissions.settings.HIDE_IN_ARCHIVE)) {
          return menuItems;
        } else {
          const headerActions: DropdownMenuItemInterface[] = [];

          if (permissions.includes(Permissions.settings.GLOBAL_SEARCH)) {
            headerActions.push({
              id: 'search',
              description: 'Zoeken',
              icon: 'profile:search',
              internalLink: isOnV2 ? '/v2/search' : '/search',
            });
          }
          if (permissions.includes(Permissions.settings.MANAGE_FAVORITES)) {
            headerActions.push({
              id: 'favorites',
              description: 'Favorieten',
              icon: 'profile:favorites',
              handler: () => {
                this.openFavoritesPanel();
              },
            });
          }
          if (permissions.includes(Permissions.settings.MANAGE_HISTORY)) {
            headerActions.push({
              id: 'history',
              description: 'Geschiedenis',
              icon: 'profile:history',
              handler: () => {
                this.openHistoryPanel();
              },
            });
          }

          return [...menuItems.slice(0, -1), ...headerActions, ...menuItems.slice(-1)];
        }
      })
    );
  }

  private onFormValueChanges(school: SchoolInterface): void {
    if (!school) return;

    this.isOnV2Route$.pipe(take(1)).subscribe((isOnV2Route) => {
      const { id: schoolId } = school;
      const url = this.router.url;
      const pageRoutes: string[] = this.getPathAfterDynamicId(url);

      const route = isOnV2Route ? ['v2', 'schools', schoolId, ...pageRoutes] : ['schools', schoolId, ...pageRoutes];
      this.router.navigate(route);
    });
  }

  private getIsOnV2SchoolDetailRoute$(): Observable<boolean> {
    return combineLatest([
      this.isOnV1Route$,
      this.router.events.pipe(startWith(new NavigationEnd(0, this.router.url, this.router.url))),
    ]).pipe(
      filter(([_, event]) => event instanceof NavigationEnd),
      map(([isOnV1Route, event]: [boolean, NavigationEnd]) => {
        if (isOnV1Route) return false;

        return this.isSchoolDetailsRoute(event.url);
      })
    );
  }

  private isSchoolDetailsRoute = (route: string) => {
    const schoolDetailRouteRegex = /^.*\/schools\/\d+(?:\/.*)?$/;
    return schoolDetailRouteRegex.test(route);
  };

  private getPathAfterDynamicId(url: string): string[] {
    const segments = url.split('/').filter((segment) => segment !== '');
    const idIndex = segments.findIndex((segment) => this.dynamicIdRegexTest(segment));

    if (idIndex === -1) return null;

    return segments.slice(idIndex + 1);
  }

  private dynamicIdRegexTest(segment: string) {
    const regex = /^\d+$/;

    return regex.test(segment);
  }

  private isHomeRoute = (route: string): boolean => {
    const homeRouteRegex = /^\/?(?:v2\/)?home$/;
    return homeRouteRegex.test(route);
  };
}
